// src/components/settings/ServicesItems.js

import React, { useState, useEffect } from 'react';
import { db, functions } from '../../firebase/config'; 
import { httpsCallable } from 'firebase/functions'; 
import { collection, query, orderBy, onSnapshot, setDoc, doc, addDoc, deleteDoc } from 'firebase/firestore';
import { Edit, Trash2, Package, Wrench, Loader2, Plus, X, Search } from 'lucide-react';
import ConfirmationModal from '../common/ConfirmationModal';

const ServicesItems = ({ currentUserProfile }) => {
    const [services, setServices] = useState([]);
    const [taxCodes, setTaxCodes] = useState([]); 
    const [isLoadingTax, setIsLoadingTax] = useState(false);

    const [newService, setNewService] = useState({ 
        name: '', 
        description: '', 
        price: '', 
        unit: 'Unit', 
        type: 'Service',
        taxCodeId: '',
        estimatedDuration: '',
    });
    
    const [editingId, setEditingId] = useState(null);
    const [message, setMessage] = useState('');
    const [serviceToDelete, setServiceToDelete] = useState(null);
    const [isFormOpen, setIsFormOpen] = useState(false); // Mobile Toggle
    const [searchTerm, setSearchTerm] = useState(''); // type-to-narrow [f2a9c6b4]

    // 1. Fetch Services
    useEffect(() => {
        if (!currentUserProfile?.organizationId) return;
        const q = query(collection(db, `organizations/${currentUserProfile.organizationId}/services`), orderBy('name'));
        const unsub = onSnapshot(q, (snapshot) => {
            setServices(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        return unsub;
    }, [currentUserProfile]);

    // 2. Fetch Tax Codes
    useEffect(() => {
        const fetchTaxCodes = async () => {
            if (!currentUserProfile?.organizationId) return;
            setIsLoadingTax(true);
            try {
                const getOptions = httpsCallable(functions, 'getQuickBooksMappingOptions');
                const result = await getOptions();
                if (result.data?.taxCodes) setTaxCodes(result.data.taxCodes);
            } catch (error) { console.error("Failed to load tax codes:", error); } 
            finally { setIsLoadingTax(false); }
        };
        fetchTaxCodes();
    }, [currentUserProfile]);

    const handleSave = async () => {
        if (!currentUserProfile?.organizationId) return;
        setMessage('');
        if (!newService.name || !newService.price || Number(newService.price) <= 0) return setMessage("Please provide a name and a valid price.");

        const dataToSave = { 
            ...newService, 
            price: Number(newService.price),
            type: newService.type || 'Service',
            taxCodeId: newService.taxCodeId || null,
            estimatedDuration: newService.estimatedDuration ? Number(newService.estimatedDuration) : null,
        };

        const servicesRef = collection(db, `organizations/${currentUserProfile.organizationId}/services`);
        if (editingId) {
            await setDoc(doc(servicesRef, editingId), dataToSave, { merge: true });
            setEditingId(null);
        } else {
            await addDoc(servicesRef, dataToSave);
        }
        setNewService({ name: '', description: '', price: '', unit: 'Unit', type: 'Service', taxCodeId: '', estimatedDuration: '' });
        setIsFormOpen(false);
    };

    const handleConfirmDelete = async () => {
        if(serviceToDelete && currentUserProfile?.organizationId) {
            await deleteDoc(doc(db, `organizations/${currentUserProfile.organizationId}/services`, serviceToDelete));
            setServiceToDelete(null);
        }
    }

    const startEdit = (service) => {
        setEditingId(service.id);
        setNewService({ ...service, type: service.type || 'Service', taxCodeId: service.taxCodeId || '', estimatedDuration: service.estimatedDuration || '' });
        setIsFormOpen(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    const cancelEdit = () => {
        setEditingId(null);
        setNewService({ name: '', description: '', price: '', unit: 'Unit', type: 'Service', taxCodeId: '', estimatedDuration: '' });
        setIsFormOpen(false);
    }

    // Type-to-narrow filter across name + description [f2a9c6b4]
    const q = searchTerm.trim().toLowerCase();
    const filteredServices = q === ''
        ? services
        : services.filter(s => {
            const haystack = `${s.name || ''} ${s.description || ''}`.toLowerCase();
            return q.split(/\s+/).every(t => haystack.includes(t));
        });

    return (
        <div className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <h2 className="text-xl font-bold text-gray-800">Services & Items</h2>
                {/* header actions: search + add [f2a9c6b4] */}
                <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
                    {services.length > 0 && (
                        <div className="flex items-center border border-gray-300 rounded-md bg-white flex-1 sm:flex-none sm:min-w-[220px]">
                            <Search size={18} className="text-gray-400 mx-2 flex-shrink-0" />
                            <input
                                type="text"
                                placeholder="Search items..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-1 pr-3 py-2 bg-transparent text-sm font-medium focus:outline-none focus:ring-0 border-0 w-full min-w-0"
                            />
                        </div>
                    )}
                    {!isFormOpen && (
                        <button onClick={() => setIsFormOpen(true)} className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors shadow-sm whitespace-nowrap">
                            <Plus size={16} /> Add Item
                        </button>
                    )}
                </div>
            </div>
            
            {/* --- ADD/EDIT FORM (Collapsible on Mobile) --- */}
            {isFormOpen && (
                <div className="mb-8 p-4 sm:p-6 bg-gray-50 rounded-xl border border-gray-200 animate-in fade-in slide-in-from-top-4">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="font-bold text-gray-700">{editingId ? 'Edit Item' : 'New Service / Material'}</h3>
                        <button onClick={cancelEdit} className="text-gray-400 hover:text-gray-600"><X size={20}/></button>
                    </div>

                    <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Name</label>
                                <input 
                                    value={newService.name} 
                                    onChange={e => setNewService({...newService, name: e.target.value})} 
                                    placeholder="e.g., Lawn Mowing" 
                                    className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" 
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Type</label>
                                <select 
                                    value={newService.type} 
                                    onChange={e => setNewService({...newService, type: e.target.value})} 
                                    className="w-full p-2.5 border border-gray-300 rounded-lg bg-white outline-none"
                                >
                                    <option value="Service">Service (Labor/Fee)</option>
                                    <option value="Material">Material (Good/Part)</option>
                                </select>
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Description</label>
                            <textarea 
                                value={newService.description} 
                                onChange={e => setNewService({...newService, description: e.target.value})} 
                                placeholder="Details for the invoice..." 
                                className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" 
                                rows="2"
                            ></textarea>
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Price ($)</label>
                                <input 
                                    type="number" 
                                    value={newService.price} 
                                    onChange={e => setNewService({...newService, price: e.target.value})} 
                                    placeholder="0.00" 
                                    className="w-full p-2.5 border border-gray-300 rounded-lg outline-none" 
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Unit</label>
                                <select 
                                    value={newService.unit || 'Unit'} 
                                    onChange={e => setNewService({...newService, unit: e.target.value})} 
                                    className="w-full p-2.5 border border-gray-300 rounded-lg bg-white outline-none"
                                >
                                    <option value="Unit">per Unit</option>
                                    <option value="Hour">per Hour</option>
                                    <option value="Ft">per Ft</option>
                                    <option value="SqFt">per SqFt</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Tax Code</label>
                                <div className="relative">
                                    <select 
                                        value={newService.taxCodeId} 
                                        onChange={e => setNewService({...newService, taxCodeId: e.target.value})} 
                                        className="w-full p-2.5 border border-gray-300 rounded-lg bg-white text-sm outline-none appearance-none"
                                        disabled={isLoadingTax}
                                    >
                                        <option value="">Default Tax</option>
                                        {taxCodes.map(code => ( <option key={code.Id} value={code.Id}>{code.Name}</option> ))}
                                    </select>
                                    {isLoadingTax && <div className="absolute right-3 top-3"><Loader2 size={14} className="animate-spin text-gray-400"/></div>}
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">
                                    Est. Duration <span className="text-gray-400 font-normal">(min)</span>
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    value={newService.estimatedDuration}
                                    onChange={e => setNewService({...newService, estimatedDuration: e.target.value})}
                                    placeholder="e.g. 60"
                                    className="w-full p-2.5 border border-gray-300 rounded-lg outline-none"
                                />
                            </div>
                        </div>

                        <div className="flex gap-3 pt-2">
                            <button onClick={handleSave} className="flex-1 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-bold shadow-sm transition-all active:scale-[0.98]">
                                {editingId ? 'Update Item' : 'Save Item'}
                            </button>
                            <button onClick={cancelEdit} className="px-6 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium">
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* --- LIST OF SERVICES --- [f2a9c6b4] */}
            <div className="space-y-3">
                {filteredServices.map(service => (
                    <div key={service.id} className="group flex flex-col sm:flex-row sm:items-center justify-between p-4 border border-gray-200 rounded-xl hover:border-blue-300 hover:shadow-sm transition-all bg-white">
                        
                        {/* Content */}
                        <div className="flex items-start gap-4 mb-3 sm:mb-0">
                            <div className={`p-3 rounded-full shrink-0 ${service.type === 'Material' ? 'bg-orange-100 text-orange-600' : 'bg-blue-100 text-blue-600'}`}>
                                {service.type === 'Material' ? <Package size={20} /> : <Wrench size={20} />}
                            </div>
                            
                            <div>
                                <div className="flex flex-wrap items-center gap-2 mb-1">
                                    <h3 className="font-bold text-gray-900 text-base">{service.name}</h3>
                                    <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wide font-bold border ${service.type === 'Material' ? 'bg-orange-50 text-orange-700 border-orange-200' : 'bg-blue-50 text-blue-700 border-blue-200'}`}>
                                        {service.type || 'Service'}
                                    </span>
                                    {service.taxCodeId && (
                                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200 font-bold">
                                            Special Tax
                                        </span>
                                    )}
                                </div>
                                <p className="text-sm text-gray-500 mb-1 line-clamp-2">{service.description}</p>
                                <p className="text-sm font-bold text-gray-800">
                                    ${Number(service.price).toLocaleString()} <span className="text-xs font-normal text-gray-500">/ {service.unit || 'Unit'}</span>
                                    {service.estimatedDuration && (
                                        <span className="ml-2 text-xs font-normal text-gray-400">· ~{service.estimatedDuration} min</span>
                                    )}
                                </p>
                            </div>
                        </div>
                        
                        {/* Actions (Stacked on Mobile, Row on Desktop) */}
                        <div className="flex items-center gap-2 sm:opacity-0 group-hover:opacity-100 transition-opacity self-end sm:self-center w-full sm:w-auto mt-2 sm:mt-0 pt-3 sm:pt-0 border-t sm:border-0 border-gray-100">
                            <button onClick={() => startEdit(service)} className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 py-2 bg-gray-50 text-gray-600 rounded-lg hover:bg-blue-50 hover:text-blue-600 font-medium text-sm transition-colors">
                                <Edit size={16}/> <span className="sm:hidden">Edit</span>
                            </button>
                            <button onClick={() => setServiceToDelete(service.id)} className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 py-2 bg-gray-50 text-gray-600 rounded-lg hover:bg-red-50 hover:text-red-600 font-medium text-sm transition-colors">
                                <Trash2 size={16}/> <span className="sm:hidden">Delete</span>
                            </button>
                        </div>
                    </div>
                ))}

                {services.length === 0 && !isFormOpen && (
                    <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-300">
                        <p className="text-gray-500 mb-2">No services or items found.</p>
                        <button onClick={() => setIsFormOpen(true)} className="text-blue-600 font-bold hover:underline">
                            Add your first item
                        </button>
                    </div>
                )}

                {/* no-results state [f2a9c6b4] */}
                {services.length > 0 && filteredServices.length === 0 && (
                    <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-300">
                        <p className="text-gray-500">No items match &ldquo;{searchTerm}&rdquo;.</p>
                    </div>
                )}
            </div>
            
            <ConfirmationModal
                isOpen={!!serviceToDelete}
                onClose={() => setServiceToDelete(null)}
                onConfirm={handleConfirmDelete}
                title="Delete Service"
                message="Are you sure you want to permanently delete this item? This cannot be undone."
                confirmText="Yes, Delete Item"
            />
        </div>
    );
};

export default ServicesItems;